import { Module } from "vuex"
import {
  ChatError,
  GameConfig,
  GlobalState,
  LiveWinnerType,
  RootState,
} from "@/store/types"
import { createAsyncAction } from "../utils/createAsyncAction"
import { getCasinoGameStatistics, getConfigs, getLiveWinners } from "../api"
import { MessageType, PinnedMessageType } from "@/components/chat/types"
import { GameSlug } from "@/services/enums"

const MESSAGES_LIMIT = 150
const CHAT_ERROR_LIMIT = 1
const LIVE_WINNERS_LIMIT = 4
const gamesStore: Module<GlobalState, RootState> = {
  namespaced: true,
  state: () => ({
    pinnedMessages: [],
    messages: [],
    globalChatErrors: [],
    globalChatBlockDuration: 0,
    globalChatBlocked: false,
    maxTabletSize: 950,
    configs: {
      data: {
        onlineUsersCount: 15,
        game: {} as { [key in GameSlug]: GameConfig },
      },
      loading: true,
    },
    liveWinners: {
      data: [],
      loading: false,
    },
    casinoGameStatistics: {
      data: [],
      loading: true,
    },
  }),
  getters: {
    data: (state) => state.configs,
    messages: (state) => state.messages,
    pinnedMessages: (state) => state.pinnedMessages,
    globalChatErrors: (state) => state.globalChatErrors,
    liveWinners: (state) => state.liveWinners,
    casinoGameStatistics: (state) => state.casinoGameStatistics,
    globalChatBlocked: (state) => state.globalChatBlocked,
    globalChatBlockDuration: (state) => state.globalChatBlockDuration,
    maxTabletSize: (state) => state.maxTabletSize,
  },
  mutations: {
    SET_ASYNC_STATE<K extends keyof GlobalState>(
      state: GlobalState,
      {
        key,
        patch,
      }: {
        key: K
        patch: Partial<GlobalState[K]>
      }
    ) {
      Object.assign(state[key], patch)
    },
    ADD_MESSAGE(state, message: MessageType) {
      state.messages.push(message)

      if (state.messages.length > MESSAGES_LIMIT) {
        state.messages.shift()
      }
      state.globalChatErrors = []
    },
    DELETE_MESSAGE(state, messageId: string) {
      state.messages = state.messages.filter(
        (message) => message.id !== messageId
      )

      const pinnedIndex = state.pinnedMessages.findIndex(
        (pinnedMessage) => pinnedMessage.id === messageId
      )
      if (pinnedIndex !== -1) {
        state.pinnedMessages.splice(pinnedIndex, 1)
      }
    },
    SET_PINNED_MESSAGE(state, message: PinnedMessageType) {
      state.pinnedMessages.unshift(message)
    },
    UNSET_PINNED_MESSAGE(state, message: PinnedMessageType) {
      state.pinnedMessages = state.pinnedMessages.filter((msg) => {
        return msg.id !== message.id
      })
    },
    SET_PINNED_MESSAGES(state, messages: PinnedMessageType[]) {
      state.pinnedMessages = messages
    },
    SET_MESSAGES(state, messages: MessageType[]) {
      state.messages = messages
    },
    CLEAR_MESSAGES(state) {
      state.messages = []
      state.globalChatErrors = []
    },
    ADD_CHAT_ERROR(state, chatError: ChatError) {
      state.globalChatErrors.push(chatError)

      if (state.globalChatErrors.length > CHAT_ERROR_LIMIT) {
        state.globalChatErrors.shift()
      }
    },
    ADD_LIVE_WINNER(state, liveWinner: LiveWinnerType) {
      state.liveWinners.data.push(liveWinner)

      if (state.liveWinners.data.length > LIVE_WINNERS_LIMIT) {
        state.liveWinners.data.shift()
      }
    },
    UPDATE_ONLINE_USERS_COUNT(state, count) {
      state.configs.data.onlineUsersCount = count + 15
    },
    UPDATE_STATISTIC(state, data) {
      const [casinoSlug, gameSlugWithSuffix] = data.key.split(":")
      const gameSlug = gameSlugWithSuffix.replace(/-(tb|op)$/, "")

      const statistic = state.casinoGameStatistics.data.find(
        (statistic) =>
          statistic.casino_game_slug === casinoSlug &&
          statistic.game_slug === gameSlug
      )

      if (statistic) {
        Object.assign(statistic, data.value)
      }
    },
    CHAT_BLOCKED(
      state,
      { duration, message }: { duration: number; message: MessageType }
    ) {
      state.globalChatBlocked = true
      state.globalChatBlockDuration = duration
      state.globalChatErrors.push(message)
      state.messages = []
      state.pinnedMessages = []
    },
    UNSET_CHAT_BLOCKED(state) {
      state.globalChatBlocked = false
      state.globalChatBlockDuration = 0
      state.globalChatErrors = []
    },
    SET_MAX_TABLET_SIZE(state, size: number) {
      state.maxTabletSize = size
    },
  },
  actions: {
    getConfigs: createAsyncAction(getConfigs, "configs"),
    getLiveWinners: createAsyncAction(getLiveWinners, "liveWinners"),
    getCasinoGameStatistics: createAsyncAction(
      getCasinoGameStatistics,
      "casinoGameStatistics"
    ),
    setMaxTabletSize({ commit }, size: number) {
      commit("SET_MAX_TABLET_SIZE", size)
    },
  },
}

export default gamesStore
